import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Checkbox, FormGroup, FormControlLabel, InputLabel, Select, MenuItem } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store'; 
import { fetchComment, markAsRead, resolveComment, updateComment } from "../../../redux/actions/comments.actions";
import { useParams, useSearchParams } from "react-router-dom";
import { CategoryLabels } from "../categoryLabels.enum";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import MDEditor from '@uiw/react-md-editor';
import { localizePal } from "../../../utils/brand";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import { PencilIcon } from "../../../components/common/Icons/Icons";

const ViewCommentModal: React.FC<ModalProps> = ({open, setOpen, object_id}) => {
    const comment_id = object_id;

    const dispatch = useDispatch<AppDispatch>();
    const { data:user, accessToken } = useSelector((state: RootState) => state.user);
    const { data:comment,error, loading } = useSelector((state: RootState) => state.comment);
    const { canAccess:canEditComment} = usePermissionCheck(['update_any_comment']);
    const { canAccess:canResolveComment} = usePermissionCheck(['mark_any_comment_as_resolved']);
    const { canAccess: isDeveloper } = useRoleCheck(['developer']);

    const {id} = useParams(); // spa id
    const searchParams = useSearchParams()[0];
    
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [category, setCategory] = useState('');
    const [resolved, setResolved] = useState(false);
    const [devOnly, setDevOnly] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canResolve, setCanResolve] = useState(false);

    /* Get current user data */
    useEffect(() => {
      if (comment_id && open){
        dispatch(fetchComment(comment_id));
      }
    }, [dispatch,comment_id,open]);

    /* Get current user data */
    useEffect(() => {
      if (comment && (open||(open&&editMode))){
        setTitle(comment.title);
        setCategory(comment.category);
        setBody(comment.body);
        setResolved(comment.is_resolved!==0);
        setDevOnly(!!comment.dev_only);
        handleRead();
        setCanEdit(comment?.user?.id===user?.id || canEditComment);
        setCanResolve(comment?.user?.id===user?.id || canResolveComment);
      }
    }, [comment, user, open,editMode,canResolveComment]);

    const handleSubmit = async () => {
      await dispatch(updateComment({
        id: comment?.id,
        title,
        category,
        body,
        is_resolved: resolved ? 1 : 0,
        ...(isDeveloper && { dev_only: devOnly }),
      }));
      if(!error){
        handleReset();
      }
    };

    const handleClose = () => {
      handleReset();
      setOpen(false);
    };

    const handleRead = async () => {
      if (comment?.is_unread) {
        await dispatch(markAsRead({id:comment?.id, spa_id:comment?.spa_id, params:searchParams.toString(), page:id}));
      }
    };

    const handleResolve = async () => {
      setResolved(!resolved);
      await dispatch(resolveComment({id:comment?.id,is_resolved:!resolved?1:0}));
    };

    const handleReset = () => {
      setTitle('');
      setBody('');
      setCategory('');
      setResolved(false);
      setDevOnly(false);
      setEditMode(false);
    };

  const colorMode = localStorage.getItem('data-theme') ?? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  const view = !editMode && loading ? "loading" : error ? "error" : editMode ? "edit" : "view";
  const categoryLabel = comment?.category ? localizePal(CategoryLabels[comment.category as keyof typeof CategoryLabels]) : undefined;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={view === "loading" || view === "error" ? "sm" : "lg"}
      busy={view === "edit" && loading}
      tone={view === "error" ? "error" : undefined}
      title={
        view === "loading" ? "Loading comment…"
        : view === "error" ? "An error occurred"
        : view === "edit" ? (loading ? "Updating comment…" : "Edit comment")
        : comment?.title
      }
      subtitle={
        view === "edit" ? <>Comment {comment?.id} · by <strong>{comment?.user?.username}</strong></>
        : view === "view" ? <>by <strong>{comment?.user?.username}</strong>{categoryLabel && <> · {categoryLabel}</>}</>
        : undefined
      }
      actions={
        view === "loading" ? undefined
        : view === "error" ?
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button className="btn btn-primary" onClick={handleReset}>Try again</button>
        </>
        : view === "edit" ?
        <>
          <button className="btn" onClick={()=>{setEditMode(false)}}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !accessToken || title===''||body===''}
          >
            {loading ? "Updating…" : "Update"}
          </button>
        </>
        :
        <>
          {canEdit && (
            <button className="btn dlg-actions-start" onClick={()=>setEditMode(true)}>
              <PencilIcon/> Edit
            </button>
          )}
          {canResolve && (
            <button className="btn" onClick={()=>handleResolve()}>{resolved?`Mark as unresolved`:`Mark as resolved`}</button>
          )}
          <button className="btn btn-primary" onClick={handleClose}>Close</button>
        </>
      }
    >
      {view === "loading" ?
        <ModalLoading/>
        : view === "error" ?
        <p>{error}</p>
        : view === "edit" ?
        <div className="dlg-section">
          <div className="field">
            <label className="field-label" htmlFor="title">Title</label>
            <input
              id="title"
              className="input"
              type="text"
              value={title??""}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={loading}
              autoComplete="off"
            />
          </div>
          <FormControl fullWidth required disabled={loading}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category??""}
              onChange={e => setCategory(e.target.value as string)}
              defaultValue=""
            >
              {Object.keys(CategoryLabels).map((cat:any, i:number)=>
                <MenuItem key={i} value={cat}>{localizePal(CategoryLabels[cat as keyof typeof CategoryLabels])}</MenuItem>
              )}
            </Select>
          </FormControl>

          <div className="field" data-color-mode={colorMode}>
            <span className="field-label">Body</span>
            <MDEditor value={body} onChange={(value) => setBody(value??"")} style={{zIndex:'99'}}/>
          </div>
          <FormGroup>
            <FormControlLabel
            label="Resolved"
            control={
              <Checkbox
                checked={resolved??false}
                onChange={(e) => setResolved(e.target.checked)}
                disabled={loading||!editMode}
              />
            } />
            {isDeveloper && (
              <FormControlLabel
                label="Dev only"
                control={
                  <Checkbox
                    checked={devOnly}
                    onChange={(e) => setDevOnly(e.target.checked)}
                    disabled={loading}
                  />
                }
              />
            )}
          </FormGroup>
        </div>
        :
        <div className="markdown-body" data-color-mode={colorMode}>
          <MDEditor.Markdown source={comment?.body} className="markdown-preview" />
        </div>
      }
    </ModalShell>
  );
};

export default ViewCommentModal;
